import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { SyncActionChildKey } from '@modules/shared/sync-center/models/sync-action-child-key';
import { SyncCenterButtonComponent } from '@modules/shared/sync-center/sync-center-button/sync-center-button.component';
import { VideoUploadResumeService } from '@modules/video-upload/video-upload-resume.service';
import { Store } from '@ngrx/store';
import { ActionObjectType } from '@shared/models/sync/action-object-type';
import { selectSyncActionDisplays } from '@shared/store/sync/sync.selectors';
import { VideoUploadReference } from '@shared/store/video-uploads/models/video-upload-reference';
import { parseSequenceParentKey } from '@shared/store/video-uploads/utils/parse-sequence-parent-key';

const stripType = (key: string): string => key.slice(key.indexOf(':') + 1);

const toVideoUploadReference = ({ parentKey, childId, type }: SyncActionChildKey): VideoUploadReference | undefined =>
  type === ActionObjectType.VIDEO ? { ...parseSequenceParentKey(stripType(parentKey)), videoId: childId } : undefined;

// Video-upload-specific wrapper around the generic sync-center button: feeds it the video-upload
// displays and routes its resume/retry/delete outputs to VideoUploadResumeService, so the button
// itself stays free of any video-upload dependency.
@Component({
  selector: 'pxc-video-upload-sync-center-button',
  templateUrl: './video-upload-sync-center-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SyncCenterButtonComponent],
})
export class VideoUploadSyncCenterButtonComponent {
  private readonly store = inject(Store);
  private readonly resumeService = inject(VideoUploadResumeService);
  protected readonly items = this.store.selectSignal(selectSyncActionDisplays);

  protected readonly onResumeChild = (childKey: SyncActionChildKey): void => {
    const reference = toVideoUploadReference(childKey);
    if (reference) {
      this.resumeService.resume(reference);
    }
  };

  protected readonly onRetryChild = (childKey: SyncActionChildKey): void => {
    const reference = toVideoUploadReference(childKey);
    if (reference) {
      this.resumeService.retry(reference);
    }
  };

  protected readonly onDeleteChild = (childKey: SyncActionChildKey): void => {
    const reference = toVideoUploadReference(childKey);
    if (reference) {
      this.resumeService.delete(reference);
    }
  };
}
